import {
  ConnectionStatusProvider,
  initializeFirebase,
  TenantProvider,
  useConnectionStatus,
  useFirebase,
  usePermissions,
  useTenant,
} from "@timebanken/firebase-context"
import ReleaseUpdateNotice from "@timebanken/react-components/features/ReleaseUpdateNotice"
import type { Role } from "@timebanken/services"
import {
  canAccessTenantRoute,
  getErrorMessage,
  isActiveMembershipStatus,
  updatePhoneShareConsent,
} from "@timebanken/services"
import type { User } from "firebase/auth"
import { httpsCallable } from "firebase/functions"
import type React from "react"
import {
  type FC,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react"
import {
  createBrowserRouter,
  type Location,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom"
import mainLogo from "../logo.svg" // Logo for the AuthPage
import logo from "../logo-alt.svg"
import { CustomerApp } from "./components/CustomerApp"
import { TenantSelectionPage } from "./pages/TenantSelectionPage"

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}
initializeFirebase(firebaseConfig)

// --- Lazy-loaded Components ---
const LoadingSkeleton = lazy(
  () => import("@timebanken/react-components/components/ui/LoadingSkeleton"),
)
const AuthPage = lazy(
  () => import("@timebanken/react-components/pages/AuthPage"),
)
const DevLoginScreen = lazy(
  () => import("@timebanken/react-components/features/DevLoginScreen"),
)
const TaskDisplay = lazy(
  () => import("@timebanken/react-components/features/TaskDisplay"),
)
const CSVImporter = lazy(
  () => import("@timebanken/react-components/components/forms/CSVImporter"),
)
const UserHoursReport = lazy(
  () => import("@timebanken/react-components/features/UserHoursReport"),
)
const UserApproval = lazy(
  () => import("@timebanken/react-components/features/UserApproval"),
)
const ResetPasswordAction = lazy(
  () => import("@timebanken/react-components/features/ResetPasswordAction"),
)
const DeactivatedUserPage = lazy(
  () => import("@timebanken/react-components/pages/DeactivatedUserPage"),
)
const TeamManagement = lazy(
  () => import("@timebanken/react-components/features/TeamManagement"),
)
const RoleManagement = lazy(
  () => import("@timebanken/react-components/features/RoleManagement"),
)
const RoleDefinitionPage = lazy(
  () => import("@timebanken/react-components/features/RoleDefinitionPage"),
)
const CalendarSyncPage = lazy(
  () => import("@timebanken/react-components/pages/CalendarSyncPage"),
)
const AnnualHoursPage = lazy(
  () => import("@timebanken/react-components/pages/AnnualHoursPage"),
)
const AdminNotificationsPage = lazy(
  () => import("@timebanken/react-components/pages/AdminNotificationsPage"),
)
const ActivityTypesPage = lazy(
  () => import("@timebanken/react-components/pages/ActivityTypesPage"),
)
const ActivityTemplatesPage = lazy(
  () => import("@timebanken/react-components/pages/ActivityTemplatesPage"),
)
const TierManagementPage = lazy(
  () => import("@timebanken/react-components/pages/TierManagementPage"),
)
const SpondImportPageV2 = lazy(
  () => import("@timebanken/react-components/pages/SpondImportPageV2"),
)
const AdminActivitiesPage = lazy(
  () => import("@timebanken/react-components/pages/AdminActivitiesPage"),
)
const AdminCampaignsContainer = lazy(
  () => import("./pages/AdminCampaignsContainer"),
)
const CampaignEmailSettingsContainer = lazy(
  () => import("./pages/CampaignEmailSettingsContainer"),
)
const AdminAutomationContainer = lazy(
  () => import("./pages/AdminAutomationContainer"),
)
const PersonsAndLinksPage = lazy(
  () => import("@timebanken/react-components/pages/PersonsAndLinksPage"),
)
const MyChildrenPage = lazy(
  () => import("@timebanken/react-components/pages/MyChildrenPage"),
)
const BillingCallbackPage = lazy(
  () => import("@timebanken/react-components/pages/BillingCallbackPage"),
)
const BillingSettingsPage = lazy(
  () => import("@timebanken/react-components/pages/BillingSettingsPage"),
)
const InvoiceListPage = lazy(
  () => import("@timebanken/react-components/pages/InvoiceListPage"),
)
const InvoiceDetailPage = lazy(
  () => import("@timebanken/react-components/pages/InvoiceDetailPage"),
)
const AccountantExportPage = lazy(
  () => import("@timebanken/react-components/pages/AccountantExportPage"),
)
const OrganizationHoursDashboardPage = lazy(
  () =>
    import("@timebanken/react-components/pages/OrganizationHoursDashboardPage"),
)
const MaintenancePage = lazy(
  () => import("@timebanken/react-components/pages/MaintenancePage"),
)
const TenantSwitcher = lazy(() =>
  import("@timebanken/react-components/components/TenantSwitcher").then(
    (module) => ({ default: module.TenantSwitcher }),
  ),
)
const Sidebar = lazy(
  () => import("@timebanken/react-components/components/navigation/Sidebar"),
)
const Header = lazy(
  () => import("@timebanken/react-components/components/navigation/Header"),
)
const ConnectionBanner = lazy(
  () => import("@timebanken/react-components/components/ui/ConnectionBanner"),
)
const PhoneConsentModal = lazy(
  () =>
    import("@timebanken/react-components/components/modals/PhoneConsentModal"),
)

// --- Helper Components ---

/**
 * Connection status banner that displays when Firebase connections are blocked
 * (e.g., by ad blockers)
 */
const ConnectionStatusBanner: FC = () => {
  const { status, dismissWarning, warningDismissed, retryConnection } =
    useConnectionStatus()

  return (
    <Suspense fallback={null}>
      <ConnectionBanner
        status={status}
        dismissed={warningDismissed}
        onDismiss={dismissWarning}
        onRetry={retryConnection}
      />
    </Suspense>
  )
}

function AppContent() {
  const firebase = useFirebase()
  const {
    databaseId,
    setDatabaseId,
    clearDatabaseId,
    availableDatabases,
    setAvailableDatabases,
    clearAvailableDatabases,
  } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const [hasCheckedDatabase, setHasCheckedDatabase] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isConsentSaving, setIsConsentSaving] = useState(false)
  const [consentError, setConsentError] = useState<string | null>(null)

  // Use permission-based route guards - must be called unconditionally
  const { can, isSuperAdmin } = usePermissions()

  const handleLoginSuccess = useCallback(
    async (user: User, devDatabaseId?: string) => {
      if (!firebase || !user.email) return

      // QA fixtures use the isolated database supplied by the existing dev-login
      // command. This selects a tenant; it never grants tenant authorization.
      if (
        devDatabaseId === "role-access-tests" &&
        window.location.hostname === "localhost" &&
        window.location.port === "3000" &&
        firebase.functions.app.options.projectId === "timebanken-qa"
      ) {
        setAvailableDatabases([
          {
            databaseId: devDatabaseId,
            customerName: "QA role access tests",
            userId: user.uid,
          },
        ])
        setDatabaseId(devDatabaseId)
        navigate("/")
        return
      }

      const findUserByEmail = httpsCallable<
        { email: string },
        { databases: typeof availableDatabases }
      >(firebase.functions, "findUserByEmail")

      try {
        const result = await findUserByEmail({ email: user.email })
        const databases = result.data.databases

        if (databases.length === 1) {
          setAvailableDatabases(databases)
          setDatabaseId(databases[0].databaseId)
          navigate("/")
        } else if (databases.length > 1) {
          setAvailableDatabases(databases)
          navigate("/select-tenant")
        } else {
          clearAvailableDatabases()
          firebase.logout()
          alert("Your user account was not found in any organization.")
        }
      } catch (error) {
        console.error("Error finding user databases:", error)
        clearAvailableDatabases()
        firebase.logout()
        alert("An error occurred while trying to log you in.")
      }
    },
    [
      firebase,
      setAvailableDatabases,
      setDatabaseId,
      navigate,
      clearAvailableDatabases,
    ],
  )

  // Automatically check for user databases when authenticated but no database selected
  useEffect(() => {
    if (
      firebase.userId &&
      firebase.user?.email &&
      !databaseId &&
      !hasCheckedDatabase
    ) {
      setHasCheckedDatabase(true)
      // If we have stored databases but no selected database, try auto-selection first
      if (availableDatabases.length === 1) {
        setDatabaseId(availableDatabases[0].databaseId)
        navigate("/")
      } else if (availableDatabases.length > 1) {
        navigate("/select-tenant")
      } else {
        // No available databases stored, fetch them
        handleLoginSuccess(firebase.user)
      }
    }

    // Reset the check flag when user logs out
    if (!firebase.userId) {
      setHasCheckedDatabase(false)
    }
  }, [
    firebase.userId,
    firebase.user,
    databaseId,
    hasCheckedDatabase,
    availableDatabases,
    handleLoginSuccess,
    navigate,
    setDatabaseId,
  ])

  const handleLogout = () => {
    clearDatabaseId() // Clear tenant selection on logout
    clearAvailableDatabases() // Clear available databases on logout
    setHasCheckedDatabase(false) // Reset database check flag
    firebase.logout()
  }

  if (!firebase.isAuthReady || firebase.isLoadingUser) {
    return <LoadingSkeleton />
  }

  if (firebase.isLoggingOut) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-[9999]">
        <p className="text-xl font-semibold animate-pulse">Logger ut...</p>
      </div>
    )
  }

  // STATE 1: User is not logged in
  if (!firebase.userId) {
    return (
      <Suspense fallback={<LoadingSkeleton />}>
        <Routes>
          <Route
            path="/auth"
            element={
              <AuthPage
                logoSrc={mainLogo}
                onLoginSuccess={handleLoginSuccess}
              />
            }
          />
          <Route path="/reset-password" element={<ResetPasswordAction />} />
          <Route path="*" element={<Navigate to="/auth" />} />
        </Routes>
      </Suspense>
    )
  }

  // STATE 2: User is logged in, but has not selected a tenant
  if (!databaseId) {
    return (
      <Suspense fallback={<LoadingSkeleton />}>
        <Routes>
          <Route path="/select-tenant" element={<TenantSelectionPage />} />
          <Route path="*" element={<Navigate to="/select-tenant" />} />
        </Routes>
      </Suspense>
    )
  }

  // STATE 3: User is logged in and has selected a tenant
  if (!isActiveMembershipStatus(firebase.userStatus)) {
    return <DeactivatedUserPage handleLogout={handleLogout} />
  }

  // GDPR Phone Consent: Block app access until user makes a consent choice.
  // Only shown when user has a phone number but hasn't made a consent decision yet.
  //
  // Browser close edge case: If the user closes the browser before making a choice,
  // no partial state is saved. The `needsPhoneConsent` flag is derived from the
  // Firestore user document (phoneShareConsent === null/undefined && phone exists),
  // so the modal will reappear on next login. Consent is only persisted after
  // `updatePhoneShareConsent` completes successfully via the handleConsent callback.
  if (firebase.needsPhoneConsent) {
    const handleConsent = async (consent: boolean) => {
      if (!firebase.userId) return
      setIsConsentSaving(true)
      setConsentError(null)
      try {
        await updatePhoneShareConsent(firebase.db, firebase.userId, consent)
        // After successful save, Firebase context will reactively update
        // needsPhoneConsent to false via the onSnapshot listener
      } catch (error) {
        const errorMsg = getErrorMessage(error)
        console.error("Failed to save phone consent:", errorMsg, error)
        setConsentError(errorMsg)
        setIsConsentSaving(false)
      }
    }

    return (
      <Suspense fallback={<LoadingSkeleton />}>
        <PhoneConsentModal
          onConsent={handleConsent}
          isLoading={isConsentSaving}
        />
        {consentError && (
          <div
            className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-danger-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3"
            role="alert"
          >
            <span>{consentError}</span>
            <button
              type="button"
              className="text-white/80 hover:text-white text-sm underline shrink-0"
              onClick={() => setConsentError(null)}
            >
              &times;
            </button>
          </div>
        )}
      </Suspense>
    )
  }

  const { userRole } = firebase

  // Permission-based route guards (usePermissions called at top of component)
  const access = (path: string) =>
    canAccessTenantRoute(path, userRole, can, isSuperAdmin)
  const canAccessTaskDisplay = access("/")
  const canAccessAdminActivities = access("/activities")
  const canAccessReport = access("/report")
  const canAccessOrganizationHours = access("/admin/organization-hours")
  const canAccessAdminUsers = access("/admin/users")
  const canAccessRoles = access("/admin/roles")
  const canAccessCampaigns = can("campaigns:send")
  const canAccessCampaignSettings = can("campaigns:settings")
  const canAccessAutomations = can("automation:manage")
  const canAccessAccountantExport = can("billing:export")

  // Determine default route based on permissions
  // All users with tasks:read should go to "/" (TaskDisplay handles showing the right view)
  const getDefaultRoute = () => {
    if (canAccessTaskDisplay) return "/"
    return "/my-relations"
  }
  const defaultRoute = getDefaultRoute()

  return (
    <div className="h-screen overflow-hidden bg-gray-100 font-sans text-gray-800">
      {/* Sidebar Navigation */}
      <Suspense fallback={null}>
        <Sidebar
          userRole={userRole}
          location={location}
          logoSrc={logo}
          onLogout={handleLogout}
          tenantSwitcher={<TenantSwitcher />}
          isMobileMenuOpen={isMobileMenuOpen}
          onMobileMenuClose={() => setIsMobileMenuOpen(false)}
          userName={
            firebase.user?.displayName ||
            (firebase.userFirstName && firebase.userLastName
              ? `${firebase.userFirstName} ${firebase.userLastName}`
              : undefined)
          }
          userEmail={firebase.user?.email || undefined}
          config={firebase.config}
        />
      </Suspense>

      {/* Main Content Area - offset by sidebar width on desktop */}
      <div className="lg:ml-64 h-full min-h-0 flex flex-col">
        <ReleaseUpdateNotice
          currentVersion={__TIMEBANKEN_BUILD_VERSION__}
          versionUrl={`${import.meta.env.BASE_URL}version.json`}
        />
        {/* Header - notification bell temporarily disabled */}
        <Suspense fallback={null}>
          <Header onMenuToggle={() => setIsMobileMenuOpen(true)} />
        </Suspense>

        <main className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 md:p-8">
          <Suspense fallback={<LoadingSkeleton />}>
            <Routes>
              {window.location.hostname === "localhost" &&
                window.location.port === "3000" &&
                firebase.functions.app.options.projectId ===
                  "timebanken-qa" && (
                  <Route
                    path="/dev-login"
                    element={
                      <DevLoginScreen onLoginSuccess={handleLoginSuccess} />
                    }
                  />
                )}
              {/* TaskDisplay: Shows AdminDashboard for admins or UserTaskView for regular users */}
              {canAccessTaskDisplay ? (
                <Route path="/" element={<TaskDisplay userRole={userRole} />} />
              ) : (
                <Route
                  path="/"
                  element={<Navigate to={defaultRoute} replace />}
                />
              )}
              {/* Admin Activities: Only for users with users:write or teams:manage */}
              {canAccessAdminActivities && (
                <Route
                  path="/activities"
                  element={<AdminActivitiesPage userRole={userRole} />}
                />
              )}
              <Route path="/my-relations" element={<MyChildrenPage />} />
              {/* Report: Users with billing:read */}
              {canAccessReport && (
                <Route path="/report" element={<UserHoursReport />} />
              )}
              {canAccessOrganizationHours && (
                <Route
                  path="/admin/organization-hours"
                  element={<OrganizationHoursDashboardPage />}
                />
              )}
              {canAccessAccountantExport && (
                <Route
                  path="/billing/accountant-export"
                  element={<AccountantExportPage />}
                />
              )}
              {/* User management: Admins and team managers */}
              {canAccessAdminUsers && (
                <Route path="/admin/users" element={<UserApproval />} />
              )}
              {/* Role management: requires roles:manage permission */}
              {canAccessRoles && (
                <>
                  <Route path="/admin/roles" element={<RoleManagement />} />
                  <Route
                    path="/admin/roles/:roleId"
                    element={
                      <RoleDefinitionPage key={location.pathname} mode="view" />
                    }
                  />
                  {(isSuperAdmin || can("roles:create")) && (
                    <Route
                      path="/admin/roles/new"
                      element={
                        <RoleDefinitionPage
                          key={location.pathname}
                          mode="new"
                        />
                      }
                    />
                  )}
                  {(isSuperAdmin || can("roles:update")) && (
                    <Route
                      path="/admin/roles/:roleId/edit"
                      element={
                        <RoleDefinitionPage
                          key={location.pathname}
                          mode="edit"
                        />
                      }
                    />
                  )}
                </>
              )}
              {canAccessCampaignSettings && (
                <Route
                  path="/admin/campaigns/settings"
                  element={<CampaignEmailSettingsContainer />}
                />
              )}
              {canAccessAutomations && (
                <Route
                  path="/admin/automation"
                  element={<AdminAutomationContainer />}
                />
              )}
              {/* Each domain page has its own permission gate. */}
              {access("/admin/persons") && (
                <Route
                  path="/admin/persons"
                  element={<PersonsAndLinksPage />}
                />
              )}
              {access("/admin/teams") && (
                <Route path="/admin/teams" element={<TeamManagement />} />
              )}
              {access("/admin/importer") && (
                <Route path="/admin/importer" element={<CSVImporter />} />
              )}
              {access("/admin/calendar-sync") && (
                <Route
                  path="/admin/calendar-sync"
                  element={<CalendarSyncPage />}
                />
              )}
              {access("/admin/activity-types") && (
                <Route
                  path="/admin/activity-types"
                  element={<ActivityTypesPage />}
                />
              )}
              {access("/admin/activity-templates") && (
                <Route
                  path="/admin/activity-templates"
                  element={<ActivityTemplatesPage />}
                />
              )}
              {access("/admin/notifications") && (
                <Route
                  path="/admin/notifications"
                  element={<AdminNotificationsPage />}
                />
              )}
              {canAccessCampaigns && (
                <>
                  <Route
                    path="/admin/campaigns"
                    element={<Navigate to="/admin/campaigns/email" replace />}
                  />
                  <Route
                    path="/admin/campaigns/email"
                    element={<AdminCampaignsContainer fixedChannel="email" />}
                  />
                  <Route
                    path="/admin/campaigns/email/:campaignId/report"
                    element={<AdminCampaignsContainer fixedChannel="email" />}
                  />
                  <Route
                    path="/admin/campaigns/sms"
                    element={<AdminCampaignsContainer fixedChannel="sms" />}
                  />
                  <Route
                    path="/admin/campaigns/sms/:campaignId/report"
                    element={<AdminCampaignsContainer fixedChannel="sms" />}
                  />
                </>
              )}
              {access("/admin/annual-hours") && (
                <Route
                  path="/admin/annual-hours"
                  element={<AnnualHoursPage />}
                />
              )}
              {isSuperAdmin && (
                <Route path="/admin/tiers" element={<TierManagementPage />} />
              )}
              {isSuperAdmin && (
                <Route
                  path="/admin/maintenance"
                  element={<MaintenancePage />}
                />
              )}
              {access("/admin/spond-import") && (
                <Route
                  path="/admin/spond-import"
                  element={<SpondImportPageV2 />}
                />
              )}
              {access("/billing/settings") && (
                <Route
                  path="/billing/settings"
                  element={<BillingSettingsPage />}
                />
              )}
              {access("/billing/settings/callback") && (
                <Route
                  path="/billing/settings/callback"
                  element={<BillingCallbackPage />}
                />
              )}
              {access("/billing/invoices") && (
                <>
                  <Route
                    path="/billing/invoices"
                    element={<InvoiceListPage />}
                  />
                  <Route
                    path="/billing/invoices/:invoiceId"
                    element={<InvoiceDetailPage />}
                  />
                </>
              )}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </main>

        <footer className="text-center text-gray-600 text-sm py-4 px-4">
          <p>
            &copy; {new Date().getFullYear()} Omnicus. Alle rettigheter
            reservert.
          </p>
        </footer>
      </div>
    </div>
  )
}

// Data routing supplies supported blockers for unsaved role drafts, including Back.
const router = createBrowserRouter([
  {
    path: "/*",
    element: (
      <>
        <ConnectionStatusBanner />
        <CustomerApp>
          <AppContent />
        </CustomerApp>
        <div id="dropdown-portal" />
      </>
    ),
  },
])

function App() {
  return (
    <ConnectionStatusProvider projectId={firebaseConfig.projectId}>
      <TenantProvider>
        <RouterProvider router={router} />
      </TenantProvider>
    </ConnectionStatusProvider>
  )
}

export default App
